import { ArrowLeft } from 'lucide-react';
import Link from 'next/link';
import type {
  ProvisionDetail,
  RegulationMetadata,
  RegulationSummary,
  RegulationTopic,
} from '@justisio/shared/ai';
import { MarkdownRenderer } from '@/components/ui/markdown-renderer';

export interface RegulationSummaryData {
  metadata: RegulationMetadata;
  executive_summary: string;
  regulated_topics: RegulationTopic[];
  transitional_provisions: string;
  sanctions: string;
  recommendations: string[];
}

export function RegulationResultSection({
  summary,
  hideHeader = false,
}: {
  summary: RegulationSummaryData;
  hideHeader?: boolean;
}) {
  if (!summary) {
    return <div className="p-4 text-center text-neutral-500">Data ringkasan tidak tersedia.</div>;
  }

  return (
    <div className="w-full space-y-8">
      {!hideHeader && (
        <>
          <div className="mb-4">
            <Link
              href="/id/regulation/summary"
              className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900 transition-colors"
            >
              <ArrowLeft size={16} /> Kembali ke Ringkasan Peraturan
            </Link>
          </div>

          <div>
            <h1 className="text-2xl font-bold capitalize mb-4">
              {summary.metadata?.title || 'Ringkasan Regulasi'}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-500">
              <span className="italic">{summary.metadata?.status || 'Status Tidak Diketahui'}</span>
              <span>Tanggal Berlaku: {summary.metadata?.effective_date || 'Tidak Diketahui'}</span>
            </div>

            {summary.metadata?.related_sectors && summary.metadata.related_sectors.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {summary.metadata.related_sectors.map((sector, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 bg-neutral-100 text-sm font-medium text-neutral-600"
                  >
                    {sector}
                  </span>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <div className={hideHeader ? '' : 'flex justify-center'}>
        <table className={`w-full ${hideHeader ? '' : 'max-w-5xl'} border-collapse`}>
          <tbody>
            <tr className="border-b border-neutral-100 flex md:table-row flex-col">
              <td className="font-bold capitalize align-top w-48 py-6 pr-6 md:table-cell block text-sm">
                Ringkasan Eksekutif
              </td>
              <td
                className={`align-top py-6 md:table-cell block ${hideHeader ? '' : 'max-w-xl'} text-justify text-sm`}
              >
                <MarkdownRenderer
                  components={{
                    h1: ({ children }) => (
                      <h1 className="text-xs font-medium text-neutral-500 mb-2 mt-1">{children}</h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-xs font-medium text-neutral-500 mb-2 mt-1">{children}</h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-xs font-medium text-neutral-500 mb-1 mt-1">{children}</h3>
                    ),
                  }}
                >
                  {summary.executive_summary || 'Deskripsi ringkasan tidak tersedia.'}
                </MarkdownRenderer>
              </td>
            </tr>
            <tr className="border-b border-neutral-100 flex md:table-row flex-col">
              <td className="font-bold capitalize align-top w-48 py-6 pr-6 md:table-cell block text-sm">
                Poin Pengaturan
              </td>
              <td className={`align-top py-6 md:table-cell block ${hideHeader ? '' : 'max-w-xl'}`}>
                {(summary.regulated_topics || []).length > 0 ? (
                  <div className="space-y-6">
                    {summary.regulated_topics.map((topic, i) => (
                      <div key={i} className="mb-6 last:mb-0">
                        <h4 className="font-semibold mb-3 capitalize text-sm">{topic.topic}</h4>
                        <ul className="space-y-2 ml-4">
                          {topic.provisions.map((provision, j) => (
                            <li key={j} className="flex gap-2 text-sm">
                              <span className="text-neutral-300 shrink-0">•</span>
                              <MarkdownRenderer
                                className="flex-1"
                                components={{
                                  h1: ({ children }) => (
                                    <h1 className="text-xs font-medium text-neutral-500 mb-2 mt-1">
                                      {children}
                                    </h1>
                                  ),
                                  h2: ({ children }) => (
                                    <h2 className="text-xs font-medium text-neutral-500 mb-2 mt-1">
                                      {children}
                                    </h2>
                                  ),
                                  h3: ({ children }) => (
                                    <h3 className="text-xs font-medium text-neutral-500 mb-1 mt-1">
                                      {children}
                                    </h3>
                                  ),
                                }}
                              >
                                {provision.description}
                              </MarkdownRenderer>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-neutral-400 italic text-sm">Tidak ada topik yang disorot.</p>
                )}
              </td>
            </tr>
            <tr className="border-b border-neutral-100 flex md:table-row flex-col">
              <td className="font-bold capitalize align-top w-48 py-6 pr-6 md:table-cell block text-sm">
                Ketentuan Peralihan
              </td>
              <td
                className={`align-top py-6 md:table-cell block ${hideHeader ? '' : 'max-w-xl'} text-justify text-sm`}
              >
                <MarkdownRenderer
                  components={{
                    h1: ({ children }) => (
                      <h1 className="text-xs font-medium text-neutral-500 mb-2 mt-1">{children}</h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-xs font-medium text-neutral-500 mb-2 mt-1">{children}</h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-xs font-medium text-neutral-500 mb-1 mt-1">{children}</h3>
                    ),
                  }}
                >
                  {summary.transitional_provisions || 'Tidak ada.'}
                </MarkdownRenderer>
              </td>
            </tr>
            <tr className="border-b border-neutral-100 flex md:table-row flex-col">
              <td className="font-bold capitalize align-top w-48 py-6 pr-6 md:table-cell block text-sm">
                Sanksi
              </td>
              <td
                className={`align-top py-6 md:table-cell block ${hideHeader ? '' : 'max-w-xl'} text-justify text-sm`}
              >
                <div className="bg-red-50 border border-red-100 p-4">
                  <MarkdownRenderer
                    components={{
                      h1: ({ children }) => (
                        <h1 className="text-xs font-medium text-neutral-500 mb-2 mt-1">
                          {children}
                        </h1>
                      ),
                      h2: ({ children }) => (
                        <h2 className="text-xs font-medium text-neutral-500 mb-2 mt-1">
                          {children}
                        </h2>
                      ),
                      h3: ({ children }) => (
                        <h3 className="text-xs font-medium text-neutral-500 mb-1 mt-1">
                          {children}
                        </h3>
                      ),
                    }}
                  >
                    {summary.sanctions || 'Tidak ada yang ditentukan.'}
                  </MarkdownRenderer>
                </div>
              </td>
            </tr>
            <tr className="flex md:table-row flex-col">
              <td className="font-bold capitalize align-top w-48 py-6 pr-6 md:table-cell block text-sm">
                Rekomendasi
              </td>
              <td className={`align-top py-6 md:table-cell block ${hideHeader ? '' : 'max-w-xl'}`}>
                {(summary.recommendations || []).length > 0 ? (
                  <ul className="space-y-2">
                    {summary.recommendations.map((rec, i) => (
                      <li key={i} className="flex gap-2 text-sm">
                        <span className="text-neutral-400 shrink-0">•</span>
                        <MarkdownRenderer
                          className="flex-1"
                          components={{
                            h1: ({ children }) => (
                              <h1 className="text-xs font-medium text-neutral-500 mb-2 mt-1">
                                {children}
                              </h1>
                            ),
                            h2: ({ children }) => (
                              <h2 className="text-xs font-medium text-neutral-500 mb-2 mt-1">
                                {children}
                              </h2>
                            ),
                            h3: ({ children }) => (
                              <h3 className="text-xs font-medium text-neutral-500 mb-1 mt-1">
                                {children}
                              </h3>
                            ),
                          }}
                        >
                          {rec}
                        </MarkdownRenderer>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-neutral-400 italic text-sm">
                    Tidak ada rekomendasi yang diberikan.
                  </p>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
